import { useMemo, useState } from 'react';
import { Badge, Button, Card, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconPaperclip, IconPlus, IconSettings, IconTool } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { MaintenanceEventList } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import { DueItemsTable } from '../maintenance/DueItemsTable';
import { MaintenanceEventDrawer } from '../maintenance/MaintenanceEventDrawer';
import { ProgrammeDrawer } from '../maintenance/ProgrammeDrawer';
import { useAircraft } from './AircraftDetailLayout';

export function AircraftMaintenanceTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const aircraft = useAircraft();
  const { can } = useSession();
  const [eventId, setEventId] = useState<number | null>(null);
  const [isEventOpen, setEventOpen] = useState(false);
  const [isProgrammeOpen, setProgrammeOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['aircraft', aircraft.registration, 'maintenance'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/{registration}/maintenance/', {
          params: { path: { registration: aircraft.registration } },
        }),
      ),
  });

  const programme = useQuery({
    queryKey: ['maintenance', 'programme', aircraft.id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/programmes/', {
          params: { query: { aircraft: aircraft.id } },
        }),
      ),
  });

  const ensureProgramme = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/maintenance/programmes/ensure/', {
          body: { aircraft: aircraft.id },
        }),
      ),
    onSuccess: async () => {
      notifySaved(t('maintenance.programme'));
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      await queryClient.invalidateQueries({ queryKey: ['aircraft', aircraft.registration] });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const eventColumns = useMemo<ColumnDef<MaintenanceEventList, unknown>[]>(
    () => [
      {
        accessorKey: 'performed_on',
        header: t('maintenance.performedOn'),
        meta: { width: 110 },
        cell: ({ row }) =>
          formatDate(row.original.performed_on) || formatDate(row.original.opened_on),
      },
      {
        accessorKey: 'title',
        header: t('common.name'),
        cell: ({ row }) => (
          <div>
            <Text fz="sm">{row.original.title}</Text>
            <Text fz="xs" c="dimmed">
              {row.original.area ? t(`maintenance.${row.original.area}`) : ''}
              {row.original.task_count ? ` · ${row.original.task_count} × ${t('maintenance.task')}` : ''}
            </Text>
          </div>
        ),
      },
      {
        accessorKey: 'performed_by_name',
        header: t('maintenance.performedBy'),
        meta: { width: 170 },
        cell: ({ row }) => (
          <Text fz="sm">
            {row.original.performed_by_name || row.original.performed_by_organisation}
          </Text>
        ),
      },
      {
        accessorKey: 'release_reference',
        header: t('maintenance.releaseReference'),
        meta: { width: 140, mono: true },
      },
      {
        id: 'files',
        header: () => <IconPaperclip size={14} />,
        meta: { align: 'center', width: 60 },
        cell: ({ row }) =>
          row.original.attachment_count ? (
            <Badge size="sm" variant="light">
              {row.original.attachment_count}
            </Badge>
          ) : null,
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        meta: { width: 120 },
        cell: ({ row }) => <StatusBadge kind="event" value={row.original.status} />,
      },
    ],
    [t],
  );

  const currentProgramme = programme.data?.results?.[0];
  const dueItems = data?.due_items ?? [];

  return (
    <Stack gap="lg">
      <div>
        <Group justify="space-between" mb="sm" wrap="wrap" gap="sm">
          <Group gap="sm">
            <Title order={3}>{t('maintenance.dueList')}</Title>
            {currentProgramme?.reference ? (
              <Tooltip label={t('maintenance.programme')}>
                <Badge variant="outline" color="gray">
                  {currentProgramme.reference}
                </Badge>
              </Tooltip>
            ) : null}
          </Group>
          {can('maintenance.manage_programme') ? (
            <Group gap="xs">
              {currentProgramme ? (
                <Button
                  variant="default"
                  leftSection={<IconSettings size={16} />}
                  onClick={() => setProgrammeOpen(true)}
                >
                  {t('maintenance.programme')}
                </Button>
              ) : (
                <Button
                  leftSection={<IconPlus size={16} />}
                  loading={ensureProgramme.isPending}
                  onClick={() => ensureProgramme.mutate()}
                >
                  {t('maintenance.populateProgramme')}
                </Button>
              )}
            </Group>
          ) : null}
        </Group>

        {dueItems.length === 0 && !isLoading && !currentProgramme ? (
          <Card p="lg">
            <Stack align="center" gap="xs">
              <IconTool size={28} opacity={0.5} />
              <Text fw={600}>{t('maintenance.programme')}</Text>
              <Text fz="sm" c="dimmed" ta="center" maw={420}>
                {t('maintenance.populateHint')}
              </Text>
              {can('maintenance.manage_programme') ? (
                <Button
                  mt="xs"
                  loading={ensureProgramme.isPending}
                  onClick={() => ensureProgramme.mutate()}
                >
                  {t('maintenance.populateProgramme')}
                </Button>
              ) : null}
            </Stack>
          </Card>
        ) : (
          <DueItemsTable items={dueItems} isLoading={isLoading} />
        )}
      </div>

      <div>
        <Group justify="space-between" mb="sm">
          <Title order={3}>{t('maintenance.log')}</Title>
          {can('maintenance.add_event') ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEventId(null);
                setEventOpen(true);
              }}
            >
              {t('maintenance.newEvent')}
            </Button>
          ) : null}
        </Group>
        <DataTable
          data={data?.recent_events ?? []}
          columns={eventColumns}
          isLoading={isLoading}
          onRowClick={(row) => {
            setEventId(row.id);
            setEventOpen(true);
          }}
          emptyTitle={t('maintenance.log')}
          minWidth={860}
        />
      </div>

      <MaintenanceEventDrawer
        opened={isEventOpen}
        onClose={() => setEventOpen(false)}
        eventId={eventId}
        aircraftId={aircraft.id}
      />

      {currentProgramme ? (
        <ProgrammeDrawer
          opened={isProgrammeOpen}
          onClose={() => setProgrammeOpen(false)}
          programmeId={currentProgramme.id}
          aircraftId={aircraft.id}
        />
      ) : null}
    </Stack>
  );
}
